import { useCallback, useEffect, useRef, useState } from "react";

interface UseResizablePanelOptions {
	/** Starting width in px. */
	initialWidth: number;
	/** Smallest allowed width in px. */
	minWidth: number;
	/** Largest allowed width in px. */
	maxWidth: number;
	/**
	 * Which edge the drag handle lives on.
	 * `"right"` (e.g. left Sidebar): dragging right grows the panel.
	 * `"left"`  (e.g. right TracePanel): dragging left grows the panel.
	 */
	edge: "left" | "right";
	/**
	 * When given, the chosen width is remembered under this localStorage key and
	 * restored on the next mount — so a panel the user sized once stays that way
	 * across navigation and reloads. Omit it (as the chat panels do) to keep the
	 * width session-only.
	 */
	storageKey?: string;
}

interface UseResizablePanelResult {
	width: number;
	isResizing: boolean;
	/** Attach to a resize handle's `onPointerDown`. */
	startResize: (event: React.PointerEvent) => void;
	/** Back to `initialWidth` — the handle's double-click, and Home/End. */
	resetWidth: () => void;
	/** Keyboard resizing: a signed pixel delta, clamped like a drag. */
	nudgeWidth: (delta: number) => void;
}

/**
 * Read a persisted width, ignoring anything that is not a usable number.
 *
 * Clamped on the way IN as well as on the way out: a width stored when the
 * window was wide must not reopen the panel wider than today's viewport, which
 * would leave a grid the user cannot see next to a panel they cannot shrink.
 */
function readStoredWidth(
	storageKey: string | undefined,
	minWidth: number,
	maxWidth: number,
): number | null {
	if (!storageKey || typeof window === "undefined") return null;
	try {
		const raw = window.localStorage.getItem(storageKey);
		if (raw === null) return null;
		const parsed = Number(raw);
		if (!Number.isFinite(parsed)) return null;
		return Math.min(maxWidth, Math.max(minWidth, parsed));
	} catch {
		// Storage can be unavailable (private mode, blocked cookies). A panel
		// that cannot remember its width is fine; one that throws is not.
		return null;
	}
}

/**
 * Drag-to-resize width state for a side panel. Width is session-only (not
 * persisted). Updates are coalesced to one per animation frame so a drag
 * re-renders the panel at most once per frame.
 */
export function useResizablePanel({
	initialWidth,
	minWidth,
	maxWidth,
	edge,
	storageKey,
}: UseResizablePanelOptions): UseResizablePanelResult {
	const [width, setWidth] = useState(
		() => readStoredWidth(storageKey, minWidth, maxWidth) ?? initialWidth,
	);
	const [isResizing, setIsResizing] = useState(false);
	const frameRef = useRef<number | null>(null);

	const startResize = useCallback(
		(event: React.PointerEvent) => {
			event.preventDefault();
			const startX = event.clientX;
			const startWidth = width;
			setIsResizing(true);

			const handleMove = (moveEvent: PointerEvent) => {
				const delta = moveEvent.clientX - startX;
				const rawWidth =
					edge === "right" ? startWidth + delta : startWidth - delta;
				const clamped = Math.min(maxWidth, Math.max(minWidth, rawWidth));
				if (frameRef.current !== null) {
					cancelAnimationFrame(frameRef.current);
				}
				frameRef.current = requestAnimationFrame(() => setWidth(clamped));
			};

			const handleUp = () => {
				setIsResizing(false);
				window.removeEventListener("pointermove", handleMove);
				window.removeEventListener("pointerup", handleUp);
				document.body.style.removeProperty("cursor");
				document.body.style.removeProperty("user-select");
			};

			window.addEventListener("pointermove", handleMove);
			window.addEventListener("pointerup", handleUp);
			// Keep a consistent resize cursor and prevent text selection while dragging.
			document.body.style.cursor = "col-resize";
			document.body.style.userSelect = "none";
		},
		[width, minWidth, maxWidth, edge],
	);

	// Persist AFTER the width settles rather than during the drag: one write per
	// resize instead of one per animation frame.
	useEffect(() => {
		if (!storageKey || isResizing || typeof window === "undefined") return;
		try {
			window.localStorage.setItem(storageKey, String(width));
		} catch {
			// See `readStoredWidth`: unavailable storage must not break resizing.
		}
	}, [storageKey, width, isResizing]);

	const resetWidth = useCallback(() => setWidth(initialWidth), [initialWidth]);

	const nudgeWidth = useCallback(
		(delta: number) =>
			setWidth((current) =>
				Math.min(maxWidth, Math.max(minWidth, current + delta)),
			),
		[minWidth, maxWidth],
	);

	useEffect(() => {
		return () => {
			if (frameRef.current !== null) {
				cancelAnimationFrame(frameRef.current);
			}
		};
	}, []);

	return { width, isResizing, startResize, resetWidth, nudgeWidth };
}
